import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useErrorToast } from "@/hooks/useErrorToast";
import {
  portfolioMatrixQueryKey,
  type PortfolioMatrixJob,
} from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { guacHttpClient } from "@/lib/http";
import { DashboardData, HttpResponse, Job, JobPriority } from "@/types";

export function useUpdateSetJobPriority() {
  const queryClient = useQueryClient();
  const errorToast = useErrorToast();

  const setDashboardPriority = (jobId: string, priority: JobPriority) => {
    queryClient.setQueriesData<DashboardData>(
      { queryKey: ["dashboard"] },
      (data) => {
        if (!data) return data;
        return {
          ...data,
          jobs: data.jobs.map((job) =>
            job.id === jobId ? { ...job, priority } : job,
          ),
        };
      },
    );
  };

  return useMutation({
    mutationFn: async ({
      jobId,
      priority,
    }: {
      jobId: string;
      priority: JobPriority;
    }) => {
      const res = await guacHttpClient.patch<
        Job,
        HttpResponse<Job>,
        { priority: JobPriority }
      >(`jobs/${jobId}/priority`, { priority });

      return res.data;
    },
    onMutate: async ({ jobId, priority }) => {
      await queryClient.cancelQueries({ queryKey: portfolioMatrixQueryKey });
      await queryClient.cancelQueries({ queryKey: ["dashboard"] });
      const previousMatrix = queryClient.getQueryData<PortfolioMatrixJob[]>(
        portfolioMatrixQueryKey,
      );
      const previousDashboards = queryClient.getQueriesData<DashboardData>({
        queryKey: ["dashboard"],
      });

      queryClient.setQueryData<PortfolioMatrixJob[]>(
        portfolioMatrixQueryKey,
        (jobs) =>
          jobs?.map((job) => (job.id === jobId ? { ...job, priority } : job)),
      );
      setDashboardPriority(jobId, priority);

      return { previousDashboards, previousMatrix };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousMatrix) {
        queryClient.setQueryData(
          portfolioMatrixQueryKey,
          context.previousMatrix,
        );
      }
      context?.previousDashboards.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      errorToast({ description: "Couldn't update the job priority." });
    },
    onSuccess: (updatedJob, { jobId }) => {
      if (!updatedJob.priority) return;
      queryClient.setQueryData<PortfolioMatrixJob[]>(
        portfolioMatrixQueryKey,
        (jobs) =>
          jobs?.map((job) =>
            job.id === jobId ? { ...job, priority: updatedJob.priority } : job,
          ),
      );
      setDashboardPriority(jobId, updatedJob.priority);
    },
  });
}
